import { FC, useCallback } from 'react';
import { useNavigate } from 'react-router';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AIChatMessage, MessageRole } from '../hooks';
import styles from './Messages.module.scss';
import { TypeMarkdown } from './TypingMarkdonw';
import { FeebackRow } from './FeebackRow';
import { parseActivities } from './parseActivities';
import { AiActivityCard } from './AiActivityCard';

export interface AIMessage extends AIChatMessage {
  insertedFrom: 'api' | 'user';
  threadId: string;
}

export const Message: FC<AIMessage> = ({
  content,
  role,
  insertedFrom,
  messageId,
  threadId
}) => {
  const navigate = useNavigate();

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      const href = e.currentTarget.getAttribute('href');
      if (!href) return;
      
      try {
        const url = new URL(href);
        
        const productionDomains = [
          'app.maletadoengenheiro.com.br',
          'app.coordly.com.br'
        ];
        
        const isProductionLink = productionDomains.some(domain => 
          url.hostname.includes(domain)
        );
        
        if (isProductionLink || url.hostname === window.location.hostname) {
          e.preventDefault();
          
          const redirectUrl = url.searchParams.get('redirectUrl');
          if (redirectUrl) {
            navigate(decodeURIComponent(redirectUrl));
            return;
          }
          
          navigate(url.pathname + url.search + url.hash);
        }
      } catch {
        if (href.startsWith('/')) {
          e.preventDefault();
          navigate(href);
        }
      }
    },
    [navigate]
  );

  const mdComponents = { a: (props: any) => <a {...props} onClick={handleLinkClick} /> };

  return (
    <div
      className={`${styles['markdown']} ${styles['MessageContainer']} ${
        styles[role === MessageRole.BOT ? 'bot' : 'user']
      }`}
    >
      {role === MessageRole.BOT && insertedFrom === 'user' ? (
        <TypeMarkdown content={content} />
      ) : (
        parseActivities(content).map((segment, idx) => {
          if (segment.type === 'activity' && segment.activity) {
            return <AiActivityCard key={idx} {...segment.activity} />;
          }
          return (
            <Markdown key={idx} remarkPlugins={[remarkGfm]} components={mdComponents}>
              {segment.text || ''}
            </Markdown>
          );
        })
      )}

      <FeebackRow
        role={role}
        content={content}
        insertedFrom={insertedFrom}
        messageId={messageId}
        threadId={threadId}
      />
    </div>
  );
};
